import React from "react";
import { PublicAquisitionData } from "./types";
import { Card } from "@/components/ui/card";
import { Icon } from "@iconify/react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { formatNumber, getSeapUrl, getMonthName } from "./utils";
import { useTranslations } from "use-intl";
type PublicAquisitionsCardProps = {
  card: PublicAquisitionData;
};
const PublicAquisitionCard = ({
  card
}: PublicAquisitionsCardProps) => {
  const t = useTranslations();
  return <Card key={card.id} className="flex w-full flex-row items-stretch justify-start gap-4 overflow-hidden rounded-3xl bg-white p-6 shadow-none transition-all desktop:justify-between" data-sentry-element="Card" data-sentry-component="PublicAquisitionCard" data-sentry-source-file="PublicAquisitionCard.tsx">
      <div className="-mx-0 -my-6 -ml-6 flex flex-col items-center justify-center border-r-2 border-t-0 px-4 py-0 pb-2 pt-0 font-semibold desktop:hidden">
        <span className="text-lg text-blue-700 dark:text-white desktop:text-2xl">
          {card.fields.date instanceof Date ? card.fields.date?.getUTCDate() : ""}
        </span>
        <span className="text-sm uppercase desktop:text-base">
          {getMonthName(card.fields.date instanceof Date ? card.fields.date?.getMonth() : 1)}
        </span>
        <span className="text-sm desktop:text-base">
          {card.fields.date instanceof Date ? card.fields.date?.getFullYear() : ""}
        </span>
      </div>
      <div className="flex w-full flex-col gap-4 desktop:gap-6">
        <div className="flex flex-col items-start gap-2">
          <div className="flex flex-col items-start gap-3 desktop:mb-2 desktop:flex-row desktop:items-center">
            {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
            <p className="rounded-3xl bg-blue-700 px-3 py-1 text-xs text-white desktop:text-base desktop:font-semibold">
              {formatNumber(parseFloat(card.fields.value))} RON /
              {formatNumber(parseFloat(card.fields.value) / 5)} EUR
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 text-base font-bold desktop:text-xl">
            {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
            <span className="flex items-center text-sm font-semibold text-blue-700 dark:text-white">
              {card.fields.type === "achizitii-directe" ? "Achizitie directa" : "Achizitie publica"}
              - {card.fields.code}
            </span>
            <span>{card.fields.name}</span>
            <p className="hidden text-sm font-semibold text-gray-700 desktop:block">
              {card.fields.cpvCodeAndName}
            </p>
          </div>
        </div>
        <div className="block w-full desktop:hidden">
          <Accordion type="single" collapsible className="w-full" data-sentry-element="Accordion" data-sentry-source-file="PublicAquisitionCard.tsx">
            <AccordionItem value="details" className="border-b-0 last:border-b-0" data-sentry-element="AccordionItem" data-sentry-source-file="PublicAquisitionCard.tsx">
              <AccordionTrigger className="w-full rounded-3xl bg-gray-100 p-4 text-sm font-semibold" data-sentry-element="AccordionTrigger" data-sentry-source-file="PublicAquisitionCard.tsx">
                {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionCard_tsx_2")}
              </AccordionTrigger>
              <AccordionContent className="mt-2 rounded-3xl bg-gray-50 p-4" data-sentry-element="AccordionContent" data-sentry-source-file="PublicAquisitionCard.tsx">
                <div className="flex flex-col items-start gap-3">
                  <p className="text-sm">
                    <span>
                      {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionCard_tsx_3")}
                    </span>
                    <span className="font-semibold">{card.fields.cpvCodeAndName}</span>
                  </p>
                  <hr className="w-full border-t border-gray-200/60" />
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-col gap-2 text-sm font-semibold desktop:flex-row desktop:items-center">
                      <Icon icon="fluent:building-48-regular" className="h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
                      {card.fields.contractingAuthorityName}
                    </span>
                    <div className="flex flex-col text-sm desktop:flex-row desktop:gap-4">
                      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                      <div className="w-full">
                        {t("locality_20230802124520")}:
                        <span className="font-semibold">{card.fields.localityAuthority}</span>
                      </div>
                      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                      <div className="w-full">
                        {t("county_20230802124521")}:{" "}
                        <span className="font-semibold">{card.fields.countyAuthority}</span>
                      </div>
                    </div>
                  </div>
                  <hr className="w-full border-t border-gray-200/60" />
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-col gap-2 text-sm font-semibold desktop:flex-row desktop:items-center">
                      <Icon icon="majesticons:suitcase-2-line" className="h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
                      {card.fields.supplierName}
                    </span>
                    <div className="flex flex-col text-sm desktop:flex-row desktop:gap-4">
                      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                      <div className="w-full">
                        {t("locality_20230802124520")}:
                        <span className="font-semibold">{card.fields.localitySupplier}</span>
                      </div>
                      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                      <div className="w-full">
                        {t("county_20230802124521")}:{" "}
                        <span className="font-semibold">{card.fields.countySupplier}</span>
                      </div>
                    </div>
                  </div>
                  <hr className="w-full border-t border-gray-200/60" />
                </div>
                <div className="mt-4">
                  <Link href={getSeapUrl(card.id!, card.index)!} passHref target="_blank" data-sentry-element="Link" data-sentry-source-file="PublicAquisitionCard.tsx">
                    <Button variant="outline" className="flex items-center gap-2 rounded-xl text-sm" data-sentry-element="Button" data-sentry-source-file="PublicAquisitionCard.tsx">
                      <Icon icon="heroicons-outline:external-link" className="h-5 w-5" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
                      {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionCard_tsx_8")}
                    </Button>
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="hidden items-end justify-between gap-20 desktop:flex">
          <div className="hidden flex-col items-start gap-4 desktop:flex desktop:flex-row desktop:gap-20">
            <div className="flex flex-col gap-1">
              <span className="flex flex-col items-start gap-2 text-sm font-semibold desktop:flex-row desktop:items-center">
                <Icon icon="fluent:building-48-regular" className="h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
                {card.fields.contractingAuthorityName}
              </span>
              <div className="flex gap-4 text-sm">
                {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                <div>
                  {t("locality_20230802124520")}:{" "}
                  <span className="font-semibold">{card.fields.localityAuthority}</span>
                </div>
                {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                <div>
                  {t("county_20230802124521")}:{" "}
                  <span className="font-semibold">{card.fields.countyAuthority}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="flex flex-col items-start gap-2 text-sm font-semibold desktop:flex-row desktop:items-center">
                <Icon icon="majesticons:suitcase-2-line" className="h-6 w-6" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
                {card.fields.supplierName}
              </span>
              <div className="flex gap-4 text-sm">
                {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                <div>
                  {t("locality_20230802124520")}:{" "}
                  <span className="font-semibold">{card.fields.localitySupplier}</span>
                </div>
                {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                <div>
                  {t("county_20230802124521")}:{" "}
                  <span className="font-semibold">{card.fields.countySupplier}</span>
                </div>
              </div>
            </div>
          </div>
          <Link href={getSeapUrl(card.id!, card.index)!} passHref target="_blank" data-sentry-element="Link" data-sentry-source-file="PublicAquisitionCard.tsx">
            <Button variant="outline" className="flex items-center gap-2 rounded-xl text-sm" data-sentry-element="Button" data-sentry-source-file="PublicAquisitionCard.tsx">
              <Icon icon="heroicons-outline:external-link" className="h-5 w-5" data-sentry-element="Icon" data-sentry-source-file="PublicAquisitionCard.tsx" />
              {t("src_app_uat_institutionSlug_transparency_public_procurement_components_PublicAquisitionCard_tsx_8")}
            </Button>
          </Link>
        </div>
      </div>
      <div className="-mx-6 hidden flex-col items-center justify-center border-t-2 py-6 pb-2 pt-6 font-semibold desktop:-mx-0 desktop:-my-6 desktop:flex desktop:border-l-2 desktop:border-t-0 desktop:px-6 desktop:py-0 desktop:pl-12 desktop:pt-0">
        <span className="text-2xl text-blue-700 dark:text-white">
          {card.fields.date instanceof Date ? card.fields.date?.getUTCDate() : ""}
        </span>
        <span className="text-base uppercase">
          {getMonthName(card.fields.date instanceof Date ? card.fields.date?.getMonth() : 1)}
        </span>
        <span className="text-base">
          {card.fields.date instanceof Date ? card.fields.date?.getFullYear() : ""}
        </span>
      </div>
    </Card>;
};
export default PublicAquisitionCard;